import axios from "axios";
import { ReqMethods, ResponseStatus } from "enums";
import env from "config/env";
import { setNotification, useNotifcation } from "context/notification";
import { useAuth, useLogout } from "./auth";


function useServiceHandler() {
  const BASE_URL = `${env.API_URL}/v1`;
  const [, dispatch] = useNotifcation();
  const Logout = useLogout();

  return (fn) =>
    async (method, path, { query, payload, token, apiKey, toaster, message, error } = {}) => {
      try {
        const res = await fn(method, path, { query, payload, token, apiKey });
        toaster &&
          setNotification(dispatch, {
            open: true,
            message: message || res.data.message,
            severity: "success",
            title: "Success",
          });

        return { response: res.data };
      } catch (err) {
        // expire error : jwt expired
        if (err.response && err.response.status === ResponseStatus.UNAUTHORIZED) {
          // setNotification(dispatch, {
          //   open: true,
          //   message: error || "session expired",
          //   title: "Error",
          //   severity: "error",
          // });
          // setTimeout(Logout, 4000);
          return { error: err.response?.data || err };
        }

        const customError = err.response?.data?.error
          ? `${err.response?.data?.message} \n${err.response?.data?.error}`
          : err.response?.data?.message;

        toaster &&
          setNotification(dispatch, {
            open: true,
            message: error || customError || err.message,
            severity: "error",
            title: "Error",
          });
        return { error: err.response ? err.response.data : err };
      }
    };
}

function useCallService() {
  const authToken = useAuth();
  const serviceHandler = useServiceHandler();
  const BASE_URL = `${env.API_URL}/v1`;

  const CallService = (method, path, { query, payload, token = true, apiKey = null }) => {
    const pathname = query ? `${path}?${query}` : path;
    const config = {};

    if (token) config.headers = { "x-auth-token": `Bearer ${authToken || token}` };
    if (apiKey) config.headers = { apiKey };

    // post/put/patch take (url, data, config); get/delete/head/options take
    // (url, config) - axios silently drops a 3rd argument on the latter, so
    // the old `Object.values` spread dropped `config` (and the auth header
    // in it) whenever `payload` was omitted on a data method.
    if (["post", "put", "patch"].includes(method)) {
      return axios[method](pathname, payload || {}, config);
    }

    if (payload) config.data = payload;
    return axios[method](pathname, config);
  };

  return serviceHandler(CallService);
}

function useAppServices() {
  const BASE_URL = `${env.API_URL}/v1`;
  const { GET, POST, PUT, DELETE } = ReqMethods;
  const CallService = useCallService();

  /**
    Option for service is the object that could has the following properties
    query, payload, token, apiKey
  */

  const APIs = {
    auth: {
      login: (options) => CallService(POST, `${BASE_URL}/auth/login`, options),
      signup: (options) => CallService(POST, `${BASE_URL}/auth/signup`, options),
      customuser: (options) => CallService(POST, `${BASE_URL}/auth/customuser`, options),
      CreateTeam: (options) => CallService(POST, `${BASE_URL}/auth/team`, options),
      EditTeam: (options) => CallService(PUT, `${BASE_URL}/auth/team`, options),
      GetTeam: (options) => CallService(GET, `${BASE_URL}/auth/team`, options),
      ForgotPassword: (options) => CallService(POST, `${BASE_URL}/auth/forget`, options),
      ResetPassword: (options) => CallService(POST, `${BASE_URL}/auth/reset`, options),
    },
    user: {
      get: (options) => CallService(GET, `${BASE_URL}/user`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/user/filter`, options),
      register: (options) => CallService(POST, `${BASE_URL}/user/register`, options),
      add_team: (options) => CallService(POST, `${BASE_URL}/user/add_team `, options),
      location_user: (options) => CallService(GET, `${BASE_URL}/user/location_user`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/user`, options),
      subscriber: (options) => CallService(GET, `${BASE_URL}/user/subscriber`, options),
      agency_user: (options) => CallService(GET, `${BASE_URL}/user/agency_user`, options),
    },
    super_admin_settings: {
      Getplugin: (options) => CallService(GET, `${BASE_URL}/assistant/functions`, options),
      get: (options) => CallService(GET, `${BASE_URL}/super_admin_settings/`, options),
    },
    integrations: {
      availableBranding: (options) =>
        CallService(GET, `${BASE_URL}/integrations/available-branding`, options),
    },
    roles: {
      create: (options) => CallService(POST, `${BASE_URL}/roles`, options),
      get: (options) => CallService(GET, `${BASE_URL}/roles`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/roles`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/roles`, options),
    },
    prompts: {
      createDocs: (options) => CallService(POST, `${BASE_URL}/prompts`, options),
      updateDocs: (options) => CallService(PUT, `${BASE_URL}/prompts`, options),
      Docs: (options) => CallService(POST, `${BASE_URL}/prompts`, options),
      get_filter: (options) => CallService(GET, `${BASE_URL}/prompts/filter`, options),
      get_admin_prompts: (options) =>
        CallService(GET, `${BASE_URL}/prompts/filter/admin-prompts`, options),
      get_filter_cat: (options) => CallService(GET, `${BASE_URL}/prompts/category/filter`, options),
      managepromptsdata: (options) =>
        CallService(GET, `${BASE_URL}/prompts/managepromptsdata`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/prompts/delete`, options),
      switchIdsManager: (options) =>
        CallService(POST, `${BASE_URL}/prompts/switchIdsManager`, options),
      promptGenerator: (options) =>
        CallService(POST, `${BASE_URL}/prompts/agency/generate`, options),
      providers: (options) => CallService(GET, `${BASE_URL}/prompts/providers`, options),
      partner: (options) => CallService(POST, `${BASE_URL}/prompts/partner`, options),
      partner_update: (options) => CallService(PUT, `${BASE_URL}/prompts/partner`, options),
      partner_get: (options) => CallService(GET, `${BASE_URL}/prompts/partner`, options),
    },
    prompts_category: {
      createDocs: (options) => CallService(POST, `${BASE_URL}/prompts/category`, options),
      updateDocs: (options) => CallService(PUT, `${BASE_URL}/prompts/category`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/prompts/category/delete`, options),
    },
    prompts_sub_category: {
      create: (options) => CallService(POST, `${BASE_URL}/prompts/subcategory`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/prompts/subcategory`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/prompts/subcategory/filter`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/prompts/subcategory/delete`, options),
    },
    manageprompt: {
      generate: (options) => CallService(POST, `${BASE_URL}/prompts/generate`, options),
      allcategory: (options) => CallService(POST, `${BASE_URL}/manageprompt/allcategory`, options),
      selectedsubcategory: (options) =>
        CallService(POST, `${BASE_URL}/manageprompt/selectedsubcategory`, options),
      create: (options) => CallService(POST, `${BASE_URL}/manageprompt`, options),
      updateprompt: (options) =>
        CallService(POST, `${BASE_URL}/manageprompt/updateprompt`, options),
      getprompts: (options) => CallService(GET, `${BASE_URL}/manageprompt/getprompts`, options),
    },
    brand: {
      get: (options) => CallService(GET, `${BASE_URL}/brand`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/brand`, options),
      UpdateLocation: (options) => CallService(PUT, `${BASE_URL}/brand/update-location`, options),
      create: (options) => CallService(POST, `${BASE_URL}/brand`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/brand/filter`, options),
    },
    app: {
      get: (options) => CallService(GET, `${BASE_URL}/app`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/app`, options),
      create: (options) => CallService(POST, `${BASE_URL}/app`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/app`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/app/filter`, options),
    },
    newsfeed: {
      get: (options) => CallService(GET, `${BASE_URL}/feeds`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/feeds/update`, options),
      create: (options) => CallService(POST, `${BASE_URL}/feeds/create-feeds`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/feeds/delete`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/feeds/filter`, options),
    },
    offer: {
      get: (options) => CallService(GET, `${BASE_URL}/offer`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/offer`, options),
      create: (options) => CallService(POST, `${BASE_URL}/offer`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/offer`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/offer/filter`, options),
    },
    accounts: {
      get: (options) => CallService(GET, `${BASE_URL}/accounts`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/accounts`, options),
      create: (options) => CallService(POST, `${BASE_URL}/accounts`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/accounts`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/accounts/filter`, options),
      search: (options) => CallService(GET, `${BASE_URL}/accounts/search`, options),
      getbyid: (options) => CallService(GET, `${BASE_URL}/accounts/getbyid`, options),
      updateaccountplugin: (options) =>
        CallService(POST, `${BASE_URL}/accounts/update-plugin`, options),
      updateAccount: (options) => CallService(PUT, `${BASE_URL}/accounts/update-account`, options),
    },
    free_account_template: {
      get: (options) => CallService(GET, `${BASE_URL}/free_account_template`, options),
      create: (options) => CallService(POST, `${BASE_URL}/free_account_template`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/free_account_template`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/free_account_template`, options),
    },
    agency_stripe: {
      get: (options) => CallService(GET, `${BASE_URL}/agency/stripe`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/agency/stripe`, options),
      create: (options) => CallService(POST, `${BASE_URL}/agency/stripe`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/agency/stripe`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/agency/stripe/filter`, options),
    },
    users: {
      get: (options) => CallService(GET, `${BASE_URL}/users`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/users`, options),
      create: (options) => CallService(POST, `${BASE_URL}/users`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/users`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/users/filter`, options),
    },
    stripe: {
      filter: (options) =>
        CallService(GET, `${BASE_URL}/agency/stripe/filter/${options.id}`, options),
      integrateAcocunt: (options) =>
        CallService(GET, `${BASE_URL}/agency/stripe/integrate`, options),
      get: (options) => CallService(GET, `${BASE_URL}/agency/stripe`, options),
      invoices: (options) =>
        CallService(GET, `${BASE_URL}/agency/stripe/invoices/${options.id}`, options),
      update: (options) =>
        CallService(POST, `${BASE_URL}/agency/stripe/update/${options.id}`, options),
      create: (options) => CallService(POST, `${BASE_URL}/agency/stripe/submit`, options),
      delete: (options) =>
        CallService(POST, `${BASE_URL}/agency/stripe/delete/${options.id}`, options),
    },
    notes: {
      get: (options) => CallService(GET, `${BASE_URL}/notes`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/notes`, options),
      create: (options) => CallService(POST, `${BASE_URL}/notes`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/notes/filter`, options),
    },
    reports: {
      customer: (options) => CallService(GET, `${BASE_URL}/reports/customer`, options),
      type_campaigns: (options) => CallService(GET, `${BASE_URL}/reports/type_campaigns`, options),
      transactions: (options) => CallService(GET, `${BASE_URL}/reports/transactions`, options),
      campaign: (options) => CallService(GET, `${BASE_URL}/reports/campaigns`, options),
      campaign_overview: (options) =>
        CallService(GET, `${BASE_URL}/reports/campaign_overview`, options),
      brand: (options) => CallService(GET, `${BASE_URL}/reports/brand`, options),
      agency: (options) => CallService(GET, `${BASE_URL}/reports/agency`, options),
      monthly_sale: (options) => CallService(GET, `${BASE_URL}/reports/monthly_sale`, options),
      agency_brands: (options) => CallService(GET, `${BASE_URL}/reports/agency_brands`, options),
    },
    Overview: {
      locations: (options) => CallService(GET, `${BASE_URL}/overview/locations`, options),
      ghlLocation: (options) => CallService(GET, `${BASE_URL}/overview/ghlLocation`, options),
      users: (options) => CallService(GET, `${BASE_URL}/overview/users`, options),
      update_location: (options) =>
        CallService(PUT, `${BASE_URL}/overview/update_location`, options),
      user_details: (options) => CallService(GET, `${BASE_URL}/overview/user_details`, options),
      updateUser: (options) => CallService(PUT, `${BASE_URL}/overview`, options),
      updatelocation: (options) => CallService(PUT, `${BASE_URL}/overview/location`, options),
    },
    customer: {
      get: (options) => CallService(GET, `${BASE_URL}/customer`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/customer`, options),
    },
    agency: {
      get: (options) => CallService(GET, `${BASE_URL}/agency`, options),
      get_app_setup: (options) => CallService(GET, `${BASE_URL}/agency/get_app_setup`, options),
      getworkflows: (options) => CallService(GET, `${BASE_URL}/agency/getworkflows`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/agency`, options),
      update_stripe: (options) => CallService(PUT, `${BASE_URL}/agency/update_stripe`, options),
      AddBalance: (options) => CallService(PUT, `${BASE_URL}/agency/addbalance`, options),
      GetChargelogs: (options) => CallService(GET, `${BASE_URL}/agency/chargelogs`, options),
      update_agency_config: (options) =>
        CallService(PUT, `${BASE_URL}/agency/prompt-config`, options),
      getProducts: (options) => CallService(GET, `${BASE_URL}/agency/getproducts`, options),
      getbyplan_byId: (options) => CallService(GET, `${BASE_URL}/agency/getbyplan_byId`, options),
      search_stripe_customers: (options) =>
        CallService(GET, `${BASE_URL}/agency/find-customers`, options),
      agentBuilderData: (options) =>
        CallService(POST, `${BASE_URL}/agency/use-agent-builder`, options),
      updateCustomFieldOptions: (options) =>
        CallService(POST, `${BASE_URL}/agency/update-customfield-options`, options),
      apply_default_settings: (options) =>
        CallService(POST, `${BASE_URL}/agency/${options.agency_id}/apply-default-settings`, options),
    },
    profile: {
      get: (options) => CallService(GET, `${BASE_URL}/profile`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/profile`, options),
      create: (options) => CallService(POST, `${BASE_URL}/profile`, options),
    },
    chat: {
      get: (options) => CallService(GET, `${BASE_URL}/chat/`, options),
      getByAgency: (options) => CallService(GET, `${BASE_URL}/chat/getbyagency`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/chat/filter`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/chat`, options),
      delete: (options) => CallService(POST, `${BASE_URL}/chat/delete`, options),
      create: (options) => CallService(POST, `${BASE_URL}/chat`, options),
      addquestion: (options) => CallService(POST, `${BASE_URL}/chat/addquestion`, options),
      getquestions: (options) => CallService(GET, `${BASE_URL}/chat/getquestions`, options),
      deleteQuestion: (options) => CallService(POST, `${BASE_URL}/chat/deleteQuestion`, options),
      editquestion: (options) => CallService(POST, `${BASE_URL}/chat/editquestion`, options),
      saveMultimedia: (options) => CallService(POST, `${BASE_URL}/chat/multimedia`, options),
      fileDownload: (options) =>
        CallService(GET, `${BASE_URL}/chat/assistants/files/download`, options),
      fileUploading: (options) =>
        CallService(POST, `${BASE_URL}/chat/assistants/files/upload`, options),
      fileDelete: (options) =>
        CallService(DELETE, `${BASE_URL}/chat/assistants/files/delete`, options),
      audio: (options) => CallService(POST, `${BASE_URL}/chat/audio`, options),
      websiteUpload: (options) =>
        CallService(POST, `${BASE_URL}/chat/assistants/website/upload`, options),
    },
    messages: {
      get: (options) => CallService(GET, `${BASE_URL}/messages`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/messages/filter`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/messages`, options),
      delete: (options) => CallService(POST, `${BASE_URL}/messages/delete`, options),
      create: (options) => CallService(POST, `${BASE_URL}/messages`, options),
    },
    webhook: {
      get: (options) => CallService(GET, `${BASE_URL}/webhook/`, options),
      checkout: (options) => CallService(GET, `${BASE_URL}/webhook/checkout`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/webhook/filter`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/webhook`, options),
      delete: (options) => CallService(POST, `${BASE_URL}/webhook/delete`, options),
      create: (options) => CallService(POST, `${BASE_URL}/webhook`, options),
    },
    tickets: {
      get: (options) => CallService(GET, `${BASE_URL}/tickets`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/tickets/filter`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/tickets`, options),
      delete: (options) => CallService(POST, `${BASE_URL}/tickets/delete`, options),
      create: (options) => CallService(POST, `${BASE_URL}/tickets`, options),
    },
    comments: {
      get: (options) => CallService(GET, `${BASE_URL}/comments`, options),
      delete: (options) => CallService(POST, `${BASE_URL}/comments/delete`, options),
      create: (options) => CallService(POST, `${BASE_URL}/comments`, options),
    },
    docs: {
      get: (options) => CallService(GET, `${BASE_URL}/docs`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/docs/filter`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/docs`, options),
      delete: (options) => CallService(POST, `${BASE_URL}/docs/delete`, options),
      create: (options) => CallService(POST, `${BASE_URL}/docs`, options),
    },
    docs_category: {
      get: (options) => CallService(GET, `${BASE_URL}/docs/category`, options),
      filter: (options) => CallService(GET, `${BASE_URL}/docs/category/filter`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/docs/category`, options),
      delete: (options) => CallService(POST, `${BASE_URL}/docs/category/delete`, options),
      create: (options) => CallService(POST, `${BASE_URL}/docs/category`, options),
    },
    location: {
      get: (options) => CallService(GET, `${BASE_URL}/location`, options),
      user_locations: (options) => CallService(GET, `${BASE_URL}/location/user_locations`, options),
    },
    business: {
      get: (options) => CallService(GET, `${BASE_URL}/business`, options),
      by_agency: (options) => CallService(GET, `${BASE_URL}/business/by_agency`, options),
      by_location: (options) => CallService(GET, `${BASE_URL}/business/by_location`, options),
    },
    design: {
      get: (options) => CallService(GET, `${BASE_URL}/design`, options),
      create: (options) => CallService(POST, `${BASE_URL}/design`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/design`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/design`, options),
    },
    campaign: {
      get: (options) => CallService(GET, `${BASE_URL}/campaign`, options),
      create: (options) => CallService(POST, `${BASE_URL}/campaign`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/campaign`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/campaign`, options),
      passes: (options) => CallService(GET, `${BASE_URL}/campaign/passes`, options),
    },
    public: {
      get: (options) => CallService(GET, `${BASE_URL}/public`, options),
      enrollment: (options) => CallService(POST, `${BASE_URL}/public/enrollment`, options),
      enroll_now: (options) => CallService(POST, `${BASE_URL}/public/enrollnow`, options),
      get_enrollment: (options) => CallService(GET, `${BASE_URL}/public/enrollment`, options),
      pass: (options) => CallService(GET, `${BASE_URL}/public/pass`, options),
      passes: (options) => CallService(GET, `${BASE_URL}/public/passes`, options),
      subscriber: (options) => CallService(GET, `${BASE_URL}/public/subscriber`, options),
    },
    pass: {
      get: (options) => CallService(GET, `${BASE_URL}/pass`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/pass`, options),
    },
    growthtool: {
      get: (options) => CallService(GET, `${BASE_URL}/growthtool`, options),
      create: (options) => CallService(POST, `${BASE_URL}/growthtool`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/growthtool`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/growthtool`, options),
    },
    redemption: {
      redeem: (options) => CallService(POST, `${BASE_URL}/redemption`, options),
      giveaway: (options) => CallService(POST, `${BASE_URL}/redemption/giveaway`, options),
    },
    mailgun: {
      getdomains: (options) => CallService(GET, `${BASE_URL}/mailgun/emails`, options),
    },
    // ghl: {
    //   call   : (options) => CallService(POST, `${BASE_URL}/services/ghl`, options),
    //   oauth : (options) => CallService(GET, `${BASE_URL}/services/ghl/oauth/callback`, options),
    // },
    super_admin: {
      get: (options) => CallService(GET, `${BASE_URL}/super_admin/`, options),
      getproducts: (options) => CallService(GET, `${BASE_URL}/super_admin/getproducts`, options),
      add_team: (options) => CallService(GET, `${BASE_URL}/super_admin/add_team`, options),
      agencies: (options) => CallService(GET, `${BASE_URL}/super_admin/agencies`, options),
      customers: (options) => CallService(GET, `${BASE_URL}/super_admin/customers`, options),
    },
    saas: {
      createSaas: (options) => CallService(POST, `${BASE_URL}/saas`, options),
      editSaas: (options) => CallService(POST, `${BASE_URL}/saas/editSaas`, options),
      getStripeProducts: (options) => CallService(GET, `${BASE_URL}/saas/get-products`, options),
      subscriptions: (options) => CallService(GET, `${BASE_URL}/saas/subscriptions`, options),
      getProductLocations: (options) =>
        CallService(GET, `${BASE_URL}/saas/getProductLocations`, options),
      potentialproducts: (options) =>
        CallService(GET, `${BASE_URL}/saas/potentialproducts`, options),
      create_accounts: (options) => CallService(POST, `${BASE_URL}/saas/create_accounts`, options),
      create_all_accounts: (options) =>
        CallService(POST, `${BASE_URL}/saas/create_all_accounts`, options),

      create_account_manually: (options) =>
        CallService(POST, `${BASE_URL}/saas/create_account_manually`, options),
      updateSaas: (options) => CallService(PUT, `${BASE_URL}/saas`, options),
      getSaas: (options) => CallService(GET, `${BASE_URL}/saas/filter`, options),
      deleteSaas: (options) => CallService(DELETE, `${BASE_URL}/saas/delete`, options),
      categorypricing: (options) => CallService(POST, `${BASE_URL}/saas/categorypricing`, options),
      fetch_category_pricing: (options) =>
        CallService(POST, `${BASE_URL}/saas/fetch_category_pricing`, options),
      agencysaas: (options) => CallService(GET, `${BASE_URL}/saas/agencysaas`, options),
      edit_category_pricing: (options) =>
        CallService(POST, `${BASE_URL}/saas/edit_category_pricing`, options),
      subscription_setting: (options) =>
        CallService(POST, `${BASE_URL}/saas/subscription_setting`, options),
      delete_category_pricing: (options) =>
        CallService(POST, `${BASE_URL}/saas/delete_category_pricing`, options),
      updateSaasConfigration: (options) =>
        CallService(POST, `${BASE_URL}/saas/updateSaasConfigration`, options),
      deleteSaasConfigration: (options) =>
        CallService(POST, `${BASE_URL}/saas/deleteSaasConfigration`, options),
      deletestripfromagency: (options) =>
        CallService(GET, `${BASE_URL}/saas/deletestripfromagency`, options),
      showonaccountlevel: (options) =>
        CallService(POST, `${BASE_URL}/saas/showonaccountlevel`, options),
      updateSaasById: (options) =>
        CallService(PUT, `${BASE_URL}/saas/update-plan-by-id`, options),
      stripkeysave: (options) => CallService(POST, `${BASE_URL}/saas/stripkeysave`, options),
    },
    aiTokenPlans: {
      list: (options) => CallService(GET, `${BASE_URL}/ai_token_plans`, options),
      create: (options) => CallService(POST, `${BASE_URL}/ai_token_plans`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/ai_token_plans`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/ai_token_plans`, options),
    },
    subscriptions: {
      upgradeSubscription: (options) =>
        CallService(PUT, `${BASE_URL}/subscriptions/upgrade-subscription`, options),
      getPaymentInfoByAgencyId: (options) => CallService(GET, `${BASE_URL}/subscriptions/agency/${options.agency_id}/${options.customer_id}`, options),

    },
    apps: {
      get: (options) => CallService(GET, `${BASE_URL}/apps/`, options),
    },
    upsales: {
      makecharge: (options) => CallService(POST, `${BASE_URL}/upsales/makecharge`, options),
      get: (options) => CallService(GET, `${BASE_URL}/upsales`, options),
    },
    product: {
      get: (options) => CallService(GET, `${BASE_URL}/product`, options),
      create: (options) => CallService(POST, `${BASE_URL}/product`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/product`, options),
      delete: (options) => CallService(DELETE, `${BASE_URL}/product`, options),
    },
    utils: {
      upload_image: (options) => CallService(POST, `${BASE_URL}/utils/upload/image`, options),
    },
    services: {
      ghl: {
        call_service: (options) => CallService(POST, `${BASE_URL}/services/ghl/`, options),
      },
    },
    ghl: {
      call: (options) => CallService(POST, `${BASE_URL}/services/ghl`, options),
      refresh_token: (options) =>
        CallService(POST, `${BASE_URL}/services/ghl/refresh_token`, options),
    },
    promptslabel: {
      create: (options) => CallService(POST, `${BASE_URL}/promptslabel/create`, options),
      Get: (options) => CallService(GET, `${BASE_URL}/promptslabel`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/promptslabel`, options),
      Delete: (options) => CallService(DELETE, `${BASE_URL}/promptslabel`, options),
      GetselectvaluesbyID: (options) =>
        CallService(GET, `${BASE_URL}/promptslabel/GetselectvaluesbyID`, options),
      addFavourite: (options) => CallService(PUT, `${BASE_URL}/promptslabel/add-favourite`, options),
    },
    superadmin_template_categories: {
      Get: (options) => CallService(GET, `${BASE_URL}/superadmin_template_categories`, options),
      save: (options) => CallService(POST, `${BASE_URL}/superadmin_template_categories/save`, options),
      Delete: (options) => CallService(DELETE, `${BASE_URL}/superadmin_template_categories`, options),
    },
    assistant: {
      Get: (options) => CallService(GET, `${BASE_URL}/modalcreate`, options),
      create: (options) => CallService(POST, `${BASE_URL}/assistant`, options),
      Getassistant: (options) => CallService(GET, `${BASE_URL}/assistant`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/assistant`, options),
      Delete: (options) => CallService(DELETE, `${BASE_URL}/assistant`, options),
      Custom: (options) => CallService(POST, `${BASE_URL}/assistant/custom`, options),
      Getplugin: (options) => CallService(GET, `${BASE_URL}/assistant/getplugins`, options),
      GetAdminAssisatntandtemplate: (options) =>
        CallService(GET, `${BASE_URL}/assistant/GetAdminAssisatntandtemplate`, options),
      getKnowledgebase: (options) =>
        CallService(GET, `${BASE_URL}/assistant/knowledgebase`, options),
      getFavourites: (options) => CallService(PUT, `${BASE_URL}/assistant/get-favourite`, options),
      addFavourite: (options) => CallService(POST, `${BASE_URL}/assistant/add-favourite`, options),
      customTaskAgent: (options) =>
        CallService(GET, `${BASE_URL}/assistant/custom-task-agent`, options),
      updateaccount: (options) => CallService(POST, `${BASE_URL}/assistant/updateaccount`, options),
      updateDefaultAssistant: (options) =>
        CallService(POST, `${BASE_URL}/assistant/update_default_assistant`, options),
      fetchurl: (options) => CallService(POST, `${BASE_URL}/assistant/fetchurl`, options),
      scrapeStatus: (options) => CallService(GET, `${BASE_URL}/assistant/scrape/status`, options),
      scrapeUrls: (options) => CallService(GET, `${BASE_URL}/assistant/scrape/urls`, options),
      mediaLibrary: (options) =>
        CallService(GET, `${BASE_URL}/assistant/files/media-library`, options),
    },
    template: {
      Get: (options) => CallService(GET, `${BASE_URL}/modalcreate`, options),
      create: (options) => CallService(POST, `${BASE_URL}/assistant_template`, options),
      Gettemplate: (options) => CallService(GET, `${BASE_URL}/assistant_template`, options),
      update: (options) => CallService(PUT, `${BASE_URL}/assistant_template`, options),
      Delete: (options) => CallService(DELETE, `${BASE_URL}/assistant_template`, options),
      Custom: (options) => CallService(POST, `${BASE_URL}/assistant_template/custom`, options),
    },
    coupons: {
      createCoupon: (options) => CallService(POST, `${BASE_URL}/coupons/`, options),
      getCoupons: (options) => CallService(GET, `${BASE_URL}/coupons`, options),
      getCouponHistory: (options) => CallService(GET, `${BASE_URL}/coupons/history`, options),
      deleteCoupon: (options) => CallService(DELETE, `${BASE_URL}/coupons/`, options),
      updateCoupon: (options) => CallService(PUT, `${BASE_URL}/coupons/`, options),
      duplicateCoupon: (options) => CallService(POST, `${BASE_URL}/coupons/duplicate`, options),
      toggleCouponStatus: (options) => CallService(PUT, `${BASE_URL}/coupons/status-toggle`, options),
    },
  };

  return APIs;
}

const useUploadImage = () => {
  const AppService = useAppServices();
  return ({ toaster, file, desiredPath }) => {
    const form = new FormData();
    form.append("image", file, file.name);
    return AppService.utils.upload_image({
      toaster,
      payload: form,
      query: `desiredPath=${desiredPath}`,
    });
  };
};

export { useAppServices, useCallService, useUploadImage };
